import React, { useState, useEffect } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  ClipboardList,
  ArrowLeft,
  Save,
  Info,
  ShieldCheck,
  Edit3,
  Calendar,
  Clock,
  MapPin,
  DoorOpen,
  FileText,
  User,
  PlusCircle,
  RefreshCcw,
  UserMinus,
  AlertCircle,
  CheckCircle2,
  Wrench
} from "lucide-react";
import { Formik, Form, Field } from "formik";
import moment from "moment";
import { editRequest, getAllRequests } from "../../store/actions";
import {
  Button,
  Input,
  Select,
  Tabs,
  Breadcrumb,
  useToast,
  Card,
  DatePicker,
  FileUpload
} from "../UI";
import { axiosConfig } from "../../config/index";
import { RequestValidationSchema } from "../../validations/index";

const EditRequest = ({ editRequest, singleRequst, history, user }: any) => {
  const { toast } = useToast();
  const [doorItems, setDoorItems] = useState([]);
  const [siteItems, setSiteItems] = useState<any>([]);
  const [searchText, setSearchText] = useState<any>("");

  useEffect(() => {
    axios.get(`/api/sites/getallsite/`, axiosConfig)
      .then((response: any) => {
        setSiteItems(response.data);
      })
      .catch((err: any) => console.error(err));
  }, []);

  useEffect(() => {
    axios.get(`/api/doors/getalldoors/`, axiosConfig)
      .then((response: any) => {
        setDoorItems(response.data);
      })
      .catch((err: any) => console.error(err));
  }, []);

  if (!singleRequst) {
    history.push("/requestlist");
    return null;
  }

  const siteOptions = siteItems.map((op: any) => ({
    label: op.sitename,
    value: op.id || op._id
  }));

  const doorOptions = doorItems.map((op: any) => ({
    label: op.alloweddoor,
    value: op.id || op._id,
    siteId: op.siteId
  }));

  const requestTypeOptions = [
    { label: "New Access", value: "new", icon: <PlusCircle className="w-4 h-4 text-[#8dc63f]" /> },
    { label: "Card Replacement", value: "replacement", icon: <RefreshCcw className="w-4 h-4 text-amber-500" /> },
    { label: "Maintenance", value: "maintenance", icon: <Wrench className="w-4 h-4 text-amber-500" /> },
    { label: "Deactivation", value: "deactivation", icon: <UserMinus className="w-4 h-4 text-rose-500" /> },
  ];

  const allowedsiteValues = singleRequst?.allowedsite?.map((site: any) => site.id || site._id) || [];
  const alloweddoorValues = singleRequst?.alloweddoor?.map((door: any) => door.id || door._id) || [];

  const tabs = [
    {
      id: 'details',
      icon: <Edit3 className="w-4 h-4" />,
      content: (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-1">
              <Field name="requesttype">
                {({ field, form, meta }: any) => (
                  <Select
                    label="Request Type"
                    placeholder="Select type..."
                    options={requestTypeOptions}
                    value={field.value}
                    onChange={(val) => form.setFieldValue("requesttype", val)}
                    error={meta.touched && meta.error ? meta.error : ""}
                    required
                  />
                )}
              </Field>

              <div className="grid grid-cols-1 gap-6">
                <Field name="requestername">
                  {({ field, form, meta }: any) => (
                    <Input
                      {...field}
                      label="Requester"
                      placeholder={user?.fullName || "Enter name..."}
                      value={user?.fullName || field.value}
                      disabled
                      leftIcon={<User className="w-4 h-4 text-slate-400" />}
                    />
                  )}
                </Field>
              </div>

              <Field name="allowedsite">
                {({ field, form, meta }: any) => (
                  <Select
                    label="Requested Sites"
                    placeholder="Select sites..."
                    options={siteOptions}
                    value={field.value}
                    onChange={(val: any[]) => {
                      form.setFieldValue("allowedsite", val);

                      // Auto-clear doors that are no longer part of the selected sites
                      const validDoorIds = doorOptions.filter((d: any) => val.includes(d.siteId)).map((d: any) => d.value);
                      const currentDoors = form.values.alloweddoor as any[] || [];
                      const updatedDoors = currentDoors.filter((id: any) => validDoorIds.includes(id));
                      if (updatedDoors.length !== currentDoors.length) {
                        form.setFieldValue("alloweddoor", updatedDoors);
                      }
                    }}
                    error={meta.touched && meta.error ? meta.error : ""}
                    multiple
                    searchable
                    required
                  />
                )}
              </Field>

              <Field name="alloweddoor">
                {({ field, form, meta }: any) => {
                  const allowedSites = form.values.allowedsite as any[] || [];
                  const activeDoorOptions = doorOptions.filter((d: any) => allowedSites.includes(d.siteId));
                  return (
                    <Select
                      label="Requested Doors"
                      placeholder={allowedSites.length === 0 ? "Select sites first..." : "Select doors..."}
                      options={activeDoorOptions}
                      value={field.value}
                      onChange={(val) => form.setFieldValue("alloweddoor", val)}
                      error={meta.touched && meta.error ? meta.error : ""}
                      multiple
                      searchable
                      required
                    />
                  );
                }}
              </Field>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field name="startdate">
                  {({ field, form, meta }: any) => (
                    <DatePicker
                      label="Start Date"
                      value={field.value}
                      onChange={(e: any) => form.setFieldValue("startdate", e.target.value)}
                      error={meta.touched && meta.error ? meta.error : ""}
                      required
                    />
                  )}
                </Field>

                <Field name="requestDuration">
                  {({ field, form, meta }: any) => (
                    <Input
                      {...field}
                      label="Duration (Expected)"
                      placeholder="days"
                      error={meta.touched && meta.error ? meta.error : ""}
                      leftIcon={<Clock className="w-4 h-4 text-slate-400" />}
                    />
                  )}
                </Field>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Supporting Documentation</label>
                <div className="p-4 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                  <Field name="reciept_attachment">
                    {({ field, form }: any) => {
                      const attachment = form.values.reciept_attachment;
                      if (attachment) {
                        const fileName = attachment instanceof File
                          ? attachment.name
                          : typeof attachment === 'string'
                            ? attachment.replace(/^.*[\\/]/, '')
                            : 'Attachment';

                        return (
                          <div className="flex flex-col items-center gap-2 text-center p-2">
                            <CheckCircle2 className="w-8 h-8 text-[#8dc63f]" />
                            <div className="space-y-0.5">
                              <p className="text-xs font-bold text-slate-900 truncate max-w-[240px]">
                                {fileName}
                              </p>
                              {attachment instanceof File && (
                                <p className="text-[10px] font-medium text-slate-400">
                                  {(attachment.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 relative z-20">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const url = attachment instanceof File
                                    ? URL.createObjectURL(attachment)
                                    : window.location.hostname === "localhost"
                                      ? `http://localhost:7271/uploads/${attachment.replace(/^.*[\\/]/, '')}`
                                      : `/uploads/${attachment.replace(/^.*[\\/]/, '')}`;
                                  window.open(url, "_blank", "noopener,noreferrer");
                                }}
                                className="text-xs text-[#8dc63f] font-bold hover:underline flex items-center gap-0.5"
                              >
                                View
                              </button>
                              <span className="text-slate-300">|</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  form.setFieldValue("reciept_attachment", null);
                                }}
                                className="text-xs text-rose-500 font-bold hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <FileUpload
                          onFileSelect={(files: any) => {
                            if (files.length > 0) {
                              form.setFieldValue("reciept_attachment", files[0]);
                            }
                          }}
                          accept=".jpg,.jpeg,.png,.pdf"
                        />
                      );
                    }}
                  </Field>
                  <p className="text-[10px] text-slate-400 mt-2 text-center font-medium">
                    Accepted formats: JPG, PNG, PDF (Max 2MB)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-3">
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Requests', to: '/requestlist' },
            { label: 'Edit Request' }
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-[#8dc63f]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Edit Request</h1>
          </div>

          <Link to="/requestlist">
            <Button variant="ghost" size="sm" className="h-8 px-3 gap-1.5 text-xs text-slate-400 hover:text-slate-900">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </Button>
          </Link>
        </div>
      </div>

      {singleRequst?.status === "Rejected" && singleRequst?.rejectionReason && (
        <div className="bg-rose-500/10 border border-rose-100/50 backdrop-blur-sm rounded-3xl p-5 shadow-sm animate-in slide-in-from-top-4 duration-300 flex items-start gap-4">
          <div className="p-2.5 bg-rose-50/10 rounded-2xl text-rose-400 shrink-0">
            <AlertCircle className="w-5 h-5 animate-pulse" />
          </div>
          <div className="space-y-1.5 flex-1">
            <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider">Rejection Remark</h3>
            <p className="text-sm font-semibold text-amber-700 leading-relaxed">{singleRequst.rejectionReason}</p>
            <p className="text-[10px] text-amber-700 font-medium">Please modify the fields below to address this remark before updating your request.</p>
          </div>
        </div>
      )}

      <Formik
        initialValues={{
          requestername: singleRequst?.requestername,
          allowedsite: allowedsiteValues,
          alloweddoor: alloweddoorValues,
          requesttype: singleRequst?.requesttype,
          startdate: singleRequst?.startdate ? moment(singleRequst.startdate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          requestDuration: singleRequst?.requestDuration || (singleRequst?.startdate && singleRequst?.enddate ? String(moment(singleRequst.enddate).diff(moment(singleRequst.startdate), 'days')) : ""),
          reciept_attachment: singleRequst?.reciept_attachment || null
        }}
        enableReinitialize={true}
        validationSchema={RequestValidationSchema}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          try {
            await editRequest(singleRequst._id || singleRequst.id, values);
            toast("Request updated successfully", "success");
            history.push("/requestlist");
          } catch (error: any) {
            toast(error.message || "Failed to update request", "error");
            setErrors(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              {tabs[0].content}
            </Card>

            <div className="flex items-center justify-end gap-3 pt-4 px-1">
              <Link to="/requestlist">
                <Button type="button" variant="ghost" className="h-12 px-8 text-slate-500 hover:text-slate-900">Cancel</Button>
              </Link>
              <Button
                type="submit"
                className="h-10 px-10 gap-2 shadow-xl shadow-[#8dc63f]/20 bg-[#8dc63f] hover:bg-[#76ab32]"
                disabled={isSubmitting}
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Saving Changes...' : 'Update Request'}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
  singleRequst: state.request.singleRequst,
});

const mapDispatchToProps = {
  editRequest,
  getAllRequests
};

export default connect(mapStateToProps, mapDispatchToProps)(EditRequest);




